import React, { useEffect, useRef, useState } from 'react';

import { normalizeTestId } from '../../utils';

interface YearDropdownProps {
  value: number;
  options: number[];
  onChange: (year: number) => void;
  id?: string;
  name?: string;
  ariaLabel?: string;
  disabled?: boolean;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

export const YearDropdown: React.FC<YearDropdownProps> = ({
  value,
  options,
  onChange,
  id,
  name,
  ariaLabel,
  disabled = false,
  'data-testid': dataTestId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const comboboxRef = useRef<HTMLDivElement>(null);

  // Find the index of the current value
  const selectedIndex = options.indexOf(value);
  const baseTestId = dataTestId ?? 'year-dropdown';
  const comboboxId = id ?? name ?? 'year';
  const labelId = `${comboboxId}-label`;
  const valueId = `${comboboxId}-value`;
  const labelledBy = ariaLabel ? `${labelId} ${valueId}` : valueId;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Scroll to selected item when opening
  useEffect(() => {
    if (isOpen && listRef.current && selectedIndex >= 0) {
      const selectedElement = listRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView?.({ block: 'nearest' });
      }
    }
  }, [isOpen, selectedIndex]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && listRef.current && highlightedIndex >= 0) {
      const highlightedElement = listRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView?.({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setHighlightedIndex(selectedIndex);
      }
    }
  };

  const handleSelect = (year: number) => {
    onChange(year);
    setIsOpen(false);
    setHighlightedIndex(-1);
    comboboxRef.current?.focus();
  };

  const listboxId = `${comboboxId}-listbox`;
  const getOptionId = (year: number) => `${comboboxId}-option-${year}`;
  const highlightedYear = isOpen && highlightedIndex >= 0 ? options[highlightedIndex] : undefined;
  const activeOptionId = highlightedYear !== undefined ? getOptionId(highlightedYear) : undefined;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setHighlightedIndex(selectedIndex);
        } else if (highlightedIndex >= 0) {
          handleSelect(options[highlightedIndex]);
        }
        break;

      case 'Escape':
        e.preventDefault();
        if (isOpen) {
          e.stopPropagation();
          setIsOpen(false);
          setHighlightedIndex(-1);
          comboboxRef.current?.focus();
        }
        break;

      case 'Tab':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;

      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setHighlightedIndex(selectedIndex);
        } else {
          setHighlightedIndex((prev) => {
            const next = prev < options.length - 1 ? prev + 1 : prev;

            return next;
          });
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setHighlightedIndex(selectedIndex);
        } else {
          setHighlightedIndex((prev) => {
            const next = prev > 0 ? prev - 1 : prev;

            return next;
          });
        }
        break;

      case 'Home':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex(0);
        }
        break;

      case 'End':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex(options.length - 1);
        }
        break;

      default:
        break;
    }
  };

  return (
    <div
      ref={dropdownRef}
      className='year-dropdown'
      data-testid={baseTestId}>
      {ariaLabel && (
        <span
          className='sr-only'
          id={labelId}>
          {ariaLabel}
        </span>
      )}
      <div
        ref={comboboxRef}
        aria-activedescendant={activeOptionId}
        aria-autocomplete='none'
        aria-controls={isOpen ? listboxId : undefined}
        aria-disabled={disabled || undefined}
        aria-expanded={isOpen}
        aria-haspopup='listbox'
        aria-labelledby={labelledBy}
        className='year-dropdown__button'
        data-testid={`${baseTestId}-button`}
        id={id}
        role='combobox'
        tabIndex={disabled ? -1 : 0}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}>
        <span id={valueId}>{value}</span>
        <svg
          aria-hidden='true'
          className='year-dropdown__arrow'
          fill='none'
          focusable='false'
          height='16'
          stroke='currentColor'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth='2'
          viewBox='0 0 24 24'
          width='16'
          xmlns='http://www.w3.org/2000/svg'>
          <polyline points='6 9 12 15 18 9' />
        </svg>
      </div>

      {/* Hidden input for form submission */}
      {name && (
        <input
          data-testid={`${baseTestId}-hidden-input`}
          name={name}
          type='hidden'
          value={value}
        />
      )}

      {isOpen && (
        <ul
          ref={listRef}
          aria-label={ariaLabel}
          className='year-dropdown__list'
          data-testid={`${baseTestId}-list`}
          id={listboxId}
          role='listbox'
          tabIndex={-1}>
          {options.map((year, index) => (
            <li
              key={year}
              aria-selected={year === value}
              className={`year-dropdown__option ${year === value ? 'year-dropdown__option--selected' : ''} ${
                index === highlightedIndex ? 'year-dropdown__option--highlighted' : ''
              }`}
              data-testid={`${baseTestId}-option-${normalizeTestId(String(year))}`}
              id={getOptionId(year)}
              role='option'
              onClick={() => handleSelect(year)}
              onMouseEnter={() => setHighlightedIndex(index)}>
              {year}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
